import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, Mail, Phone, MapPin, Facebook, Twitter, Instagram, Linkedin } from 'lucide-react';
import axios from 'axios';

const Footer = () => {
  const [footerSettings, setFooterSettings] = useState(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchFooterSettings();
  }, []);

  const fetchFooterSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/footer-settings`);
      setFooterSettings(response.data);
    } catch (error) {
      console.error('Error fetching footer settings:', error);
      // Use default if API fails
      setFooterSettings({
        sections: [
          {
            title: "Resources",
            links: [
              { text: "JEE Main Colleges", url: "/colleges/jee-main" },
              { text: "JEE Advanced Colleges", url: "/colleges/jee-advanced" },
              { text: "IIT Cutoffs", url: "/cutoffs/iit" },
              { text: "NIT Cutoffs", url: "/cutoffs/nit" },
              { text: "IIIT Cutoffs", url: "/cutoffs/iiit" },
              { text: "Blog", url: "/blog" }
            ]
          }
        ],
        copyrightText: "© 2024 College Kampus. All rights reserved.",
        socialLinks: {
          facebook: "https://facebook.com/collegekampus",
          twitter: "https://twitter.com/collegekampus",
          instagram: "https://instagram.com/collegekampus",
          linkedin: "https://linkedin.com/company/collegekampus"
        }
      });
    }
  };

  // Always show footer - use defaults if settings haven't loaded yet
  const settings = footerSettings || {
    sections: [
      {
        title: "Resources",
        links: [
          { text: "JEE Main Colleges", url: "/colleges/jee-main" },
          { text: "JEE Advanced Colleges", url: "/colleges/jee-advanced" },
          { text: "IIT Cutoffs", url: "/cutoffs/iit" },
          { text: "NIT Cutoffs", url: "/cutoffs/nit" }
        ]
      }
    ],
    copyrightText: "© 2024 College Kampus. All rights reserved.",
    socialLinks: {
      facebook: "https://facebook.com/collegekampus",
      twitter: "https://twitter.com/collegekampus",
      instagram: "https://instagram.com/collegekampus",
      linkedin: "https://linkedin.com/company/collegekampus"
    }
  };

  return (
    <footer className="bg-slate-900 text-slate-300 mt-auto">
      <div className="container mx-auto px-4 py-12">
        <div className="grid md:grid-cols-4 gap-8">
          {/* Brand Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-lg flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">College Kampus</h3>
                <p className="text-xs text-slate-400">JEE College Predictor</p>
              </div>
            </div>
            <p className="text-sm text-slate-400">
              Your trusted companion for JEE college predictions. Get accurate admission chances with our AI-powered analysis.
            </p>
            <div className="flex gap-3">
              <a href={settings.socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="w-8 h-8 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-blue-600 transition-colors">
                <Facebook className="w-4 h-4" />
              </a>
              <a href={settings.socialLinks.twitter} target="_blank" rel="noopener noreferrer" className="w-8 h-8 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-blue-400 transition-colors">
                <Twitter className="w-4 h-4" />
              </a>
              <a href={settings.socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="w-8 h-8 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-pink-600 transition-colors">
                <Instagram className="w-4 h-4" />
              </a>
              <a href={settings.socialLinks.linkedin} target="_blank" rel="noopener noreferrer" className="w-8 h-8 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-blue-700 transition-colors">
                <Linkedin className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-sm hover:text-blue-400 transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/about-us" className="text-sm hover:text-blue-400 transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/login" className="text-sm hover:text-blue-400 transition-colors">
                  Login / Register
                </Link>
              </li>
              <li>
                <Link to="/my-predictions" className="text-sm hover:text-blue-400 transition-colors">
                  My Predictions
                </Link>
              </li>
              <li>
                <Link to="/referral" className="text-sm hover:text-blue-400 transition-colors">
                  Referral Program
                </Link>
              </li>
            </ul>
          </div>

          {/* Dynamic Sections from Admin - Only show first section (Resources) */}
          {settings.sections.slice(0, 1).map((section, index) => (
            <div key={index}>
              <h4 className="text-white font-semibold mb-4">{section.title}</h4>
              <ul className="space-y-2">
                {section.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <a href={link.url} className="text-sm hover:text-blue-400 transition-colors">
                      {link.text}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-semibold mb-4">Contact Us</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-2">
                <Mail className="w-4 h-4 text-blue-400 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-sm">Email</p>
                  <a href="mailto:contact@collegekampus.com" className="text-sm hover:text-blue-400 transition-colors">
                    contact@collegekampus.com
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <Phone className="w-4 h-4 text-blue-400 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-sm">Phone</p>
                  <a href="tel:+919056161621" className="text-sm hover:text-blue-400 transition-colors">
                    +91 9056161621
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-blue-400 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-sm">Address</p>
                  <p className="text-sm">
                    Chandigarh, India
                  </p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar with Legal Links */}
        <div className="border-t border-slate-800 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-slate-400">
              {settings.copyrightText}
            </p>
            <div className="flex gap-6">
              <a href="/privacy" className="text-sm hover:text-blue-400 transition-colors">
                Privacy Policy
              </a>
              <a href="/terms" className="text-sm hover:text-blue-400 transition-colors">
                Terms of Service
              </a>
              <a href="/disclaimer" className="text-sm hover:text-blue-400 transition-colors">
                Disclaimer
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;

